import { c as _c } from "react/compiler-runtime";
import { useContext } from 'react';
import useSWR from 'swr';
import { APIList } from '../../../../../../../typings/API.interface';
import { Bookmark } from '../../../../../../../typings/Bookmark.interface';
import { Event } from '../../../../../../../typings/Event.interface';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import Button from '../../../common/base/Button';
import Chip from '../../../common/base/Chip';
import Loading from '../../../common/base/Loading';
import Text from '../../../common/base/Text';
import DisplayDate from '../../../common/DisplayDate';
import { Div } from '../../../common/helpers/StyledUtils';
import { Status } from '../../../common/Status';
import Table from '../../../common/Table/Table';
import { GlobalContext } from '../../../contexts/GlobalContext';
import BookmarksListEmptyView from './BookmarksListEmptyView';
import Skeleton from '../../../common/base/Skeleton';
type BookmarksListProps = {
  bookmarks?: APIList<Bookmark>;
  events?: APIList<Event>;
  active_filters?: object;
  selected_event_id?: string;
  selected_id: string;
  onEventSelected(id: string): void;
  onBookmarkSelected(id: string | null): void;
  onFilterChanged(filters: object): void;
  onReplay: (bookmark: Bookmark) => void;
};
const BookmarksList: React.FC<BookmarksListProps> = t0 => {
  const $ = _c(37);
  const {
    bookmarks,
    events,
    selected_event_id,
    onEventSelected,
    selected_id,
    onBookmarkSelected,
    onFilterChanged,
    onReplay
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const entries = bookmarks?.models;
  const count = bookmarks?.count;
  let t1;
  let t2;
  let unique_webhook_ids;
  if ($[0] !== entries || $[1] !== events?.models) {
    const webhook_ids = new Set();
    entries?.forEach(entry => webhook_ids.add(entry.webhook_id));
    events?.models.forEach(event => webhook_ids.add(event.webhook_id));
    unique_webhook_ids = Array.from(webhook_ids).sort();
    t1 = useSWR;
    t2 = unique_webhook_ids.length > 0 && APIMethodKeys.webhooks.list({
      id: unique_webhook_ids
    });
    $[0] = entries;
    $[1] = events?.models;
    $[2] = t1;
    $[3] = t2;
    $[4] = unique_webhook_ids;
  } else {
    t1 = $[2];
    t2 = $[3];
    unique_webhook_ids = $[4];
  }
  let t3;
  if ($[5] !== HookdeckAPI.webhooks || $[6] !== unique_webhook_ids) {
    t3 = () => HookdeckAPI.webhooks.list({
      id: unique_webhook_ids
    });
    $[5] = HookdeckAPI.webhooks;
    $[6] = unique_webhook_ids;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      keepPreviousData: true
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    data: webhooks
  } = t1(t2, t3, t4);
  let t5;
  if ($[9] !== webhooks) {
    t5 = webhooks && webhooks.models.reduce(_temp, {});
    $[9] = webhooks;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const webhooks_by_id = t5;
  if (entries === undefined || unique_webhook_ids.length > 0 && webhooks === undefined) {
    let t6;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <Div w={100} p={{
        y: 12
      }} flex={{
        justify: "center "
      }}><Loading /></Div>;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    return t6;
  }
  if (entries && count === 0) {
    let t6;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <BookmarksListEmptyView />;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    return t6;
  }
  let t6;
  if ($[13] !== onBookmarkSelected || $[14] !== onEventSelected) {
    t6 = id => id ? id.indexOf("evt_") === 0 ? onEventSelected(id) : onBookmarkSelected(id) : onBookmarkSelected(null);
    $[13] = onBookmarkSelected;
    $[14] = onEventSelected;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const onRowSelected = t6;
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = ["Bookmark Label", "Connection", "Last Used"];
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const headers = t7;
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = [{
      min: 280,
      max: 280
    }, {
      min: 120
    }, {
      min: 250,
      max: 250
    }];
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const widths = t8;
  let t9;
  if ($[18] !== entries || $[19] !== events || $[20] !== onReplay || $[21] !== selected_event_id || $[22] !== selected_id || $[23] !== webhooks_by_id) {
    t9 = entries?.reduce((rows, entry_0) => {
      const webhook_0 = webhooks_by_id ? webhooks_by_id[entry_0?.webhook_id] : undefined;
      return [...rows, {
        id: entry_0.id,
        selected: selected_id === entry_0.id && !selected_event_id,
        highlighted: selected_id === entry_0.id,
        outdated: false,
        expendable: true,
        fields: [entry_0.label, webhook_0 ? <Chip to={`/connections/${webhook_0.id}`} outline={true} mono={true} label={webhook_0.full_name} /> : <Text mono={true} size="s" muted={true}>Connections deleted</Text>, <Div key={`${entry_0.id}-next-attempt`} flex={{
          gap: 2,
          justify: "space-between",
          align: "center"
        }}>{!entry_0.last_used_at ? <Text size="s">–</Text> : <DisplayDate date={entry_0.last_used_at} show_timezone={false} />}<Button.Permission role="member" ghost={true} small={true} icon="replay" onClick={e => {
            e.stopPropagation();
            onReplay(entry_0);
          }}>Replay</Button.Permission></Div>]
      }, ...(selected_id !== entry_0.id ? [] : [{
        headers: ["Status", "Connection", "Event Date"],
        rows: !events ? [{
          id: entry_0.id,
          selected: false,
          highlighted: false,
          selectable: false,
          expendable: false,
          fields: [<Skeleton h={{
            px: 20
          }} w={{
            px: 160
          }} key="1" />, <Skeleton h={{
            px: 20
          }} w={{
            px: 200
          }} key="2" />, <Skeleton h={{
            px: 20
          }} w={{
            px: 140
          }} key="3" />]
        }] : events.models.length === 0 ? [{
          id: "none",
          highlighted: true,
          selectable: false,
          element: <Div p={{
            x: 5,
            y: 2
          }} style={{
            gridColumn: "1 / -1"
          }}><Text muted={true}>No events associated with this bookmark in your retention window.</Text></Div>
        }] : events.models.map(event_0 => ({
          id: event_0.id,
          selected: selected_event_id === event_0.id,
          highlighted: selected_id === entry_0.id,
          fields: [<Status key={event_0.id} {...event_0} />, webhooks_by_id[event_0.webhook_id] ? <Chip to={`/connections/${event_0.webhook_id}`} mono={true} outline={true} label={webhooks_by_id[event_0.webhook_id].full_name} /> : <Text mono={true} size="s" muted={true}>Connections deleted</Text>, <DisplayDate key={event_0.id} date={event_0.created_at} show_timezone={false} />]
        }))
      }])];
    }, []);
    $[18] = entries;
    $[19] = events;
    $[20] = onReplay;
    $[21] = selected_event_id;
    $[22] = selected_id;
    $[23] = webhooks_by_id;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const rows_0 = t9;
  let t10;
  if ($[25] !== bookmarks || $[26] !== onFilterChanged) {
    t10 = bookmarks?.pagination.next ? () => onFilterChanged({
      next: bookmarks.pagination.next,
      prev: undefined
    }) : undefined;
    $[25] = bookmarks;
    $[26] = onFilterChanged;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== bookmarks || $[29] !== onFilterChanged) {
    t11 = bookmarks?.pagination.prev ? () => onFilterChanged({
      prev: bookmarks.pagination.prev,
      next: undefined
    }) : undefined;
    $[28] = bookmarks;
    $[29] = onFilterChanged;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== count || $[32] !== onRowSelected || $[33] !== rows_0 || $[34] !== t10 || $[35] !== t11) {
    t12 = <Table fill={true} top_offset={52} headers={headers} widths={widths} rows={rows_0} onRowSelected={onRowSelected} onNextPage={t10} onPreviousPage={t11} current_count={count} />;
    $[31] = count;
    $[32] = onRowSelected;
    $[33] = rows_0;
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  return t12;
};
export default BookmarksList;
function _temp(object, webhook) {
  return {
    ...object,
    [webhook.id]: webhook
  };
}